import { readLocalStorage, writeLocalStorage } from "./localStoragePreference.ts";

/** Orifices/Weirs/Outlets each keep their OWN id prefix rather than sharing one combined "Regulators" field — every existing call site/test assumes separate OR1/W1/OL1 naming, and merging them would break that. */
export interface ElementDefaults {
  idPrefixes: {
    raingage: string;
    subcatchment: string;
    junction: string;
    outfall: string;
    divider: string;
    storage: string;
    conduit: string;
    pump: string;
    orifice: string;
    weir: string;
    outlet: string;
  };
  idIncrement: number;
  subcatchment: {
    area: number;
    imperviousPercent: number;
    width: number;
    slopePercent: number;
    nImperv: number;
    nPerv: number;
    dStoreImperv: number;
    dStorePerv: number;
    pctZeroImperv: number;
  };
  node: {
    invert: number;
    maxDepth: number;
    pondedArea: number;
  };
  conduit: {
    length: number;
    roughness: number;
  };
}

export const DEFAULT_ELEMENT_DEFAULTS: ElementDefaults = {
  idPrefixes: {
    raingage: "RG",
    subcatchment: "S",
    junction: "J",
    outfall: "O",
    divider: "DV",
    storage: "ST",
    conduit: "C",
    pump: "PU",
    orifice: "OR",
    weir: "W",
    outlet: "OL",
  },
  idIncrement: 1,
  subcatchment: {
    area: 0,
    imperviousPercent: 0,
    width: 0,
    slopePercent: 0,
    nImperv: 0,
    nPerv: 0,
    dStoreImperv: 0,
    dStorePerv: 0,
    pctZeroImperv: 0,
  },
  node: { invert: 0, maxDepth: 0, pondedArea: 0 },
  conduit: { length: 400, roughness: 0.01 },
};

const LOCAL_STORAGE_KEY = "swmm-element-defaults";

function numberOrDefault(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Same as `numberOrDefault`, but a stored 0 (or negative) also falls back — `idIncrement` feeds `nextAvailableName`'s own increment step, and a 0 step there loops forever. */
function positiveNumberOrDefault(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

function stringOrDefault(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

/** Every key merged individually, falling back to `defaults`' own value for that one key on a bad/missing shape — same "one bad value, one fallback" spirit as `parseNum`, just for JSON rather than `.inp` tokens. */
function mergeNumericGroup<T extends Record<string, number>>(defaults: T, parsed: unknown): T {
  const source = asRecord(parsed);
  const result = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T)[]) {
    result[key] = numberOrDefault(source[key as string], defaults[key]) as T[keyof T];
  }
  return result;
}

function mergeIdPrefixes(
  defaults: ElementDefaults["idPrefixes"],
  parsed: unknown
): ElementDefaults["idPrefixes"] {
  const source = asRecord(parsed);
  const result = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof typeof defaults)[]) {
    result[key] = stringOrDefault(source[key as string], defaults[key]);
  }
  return result;
}

/**
 * `JSON.parse`d `localStorage` content is `unknown`, not `ElementDefaults` —
 * a hand-edited or stale-schema value (e.g. `idIncrement` saved as a string)
 * would otherwise flow silently into every `.inp` row this feeds
 * (`nextAvailableName`, new-element property panels), producing a malformed
 * file with no warning. Every field is validated/coerced against its
 * expected JS type individually, falling back to `DEFAULT_ELEMENT_DEFAULTS`'
 * own value for just that field on a mismatch, rather than trusting the
 * whole parsed shape.
 */
export function readSavedElementDefaults(): ElementDefaults {
  const raw = readLocalStorage(LOCAL_STORAGE_KEY);
  if (!raw) return DEFAULT_ELEMENT_DEFAULTS;
  try {
    const parsed = asRecord(JSON.parse(raw));
    return {
      idPrefixes: mergeIdPrefixes(DEFAULT_ELEMENT_DEFAULTS.idPrefixes, parsed.idPrefixes),
      idIncrement: positiveNumberOrDefault(
        parsed.idIncrement,
        DEFAULT_ELEMENT_DEFAULTS.idIncrement
      ),
      subcatchment: mergeNumericGroup(DEFAULT_ELEMENT_DEFAULTS.subcatchment, parsed.subcatchment),
      node: mergeNumericGroup(DEFAULT_ELEMENT_DEFAULTS.node, parsed.node),
      conduit: mergeNumericGroup(DEFAULT_ELEMENT_DEFAULTS.conduit, parsed.conduit),
    };
  } catch {
    return DEFAULT_ELEMENT_DEFAULTS;
  }
}

export function saveElementDefaults(defaults: ElementDefaults): void {
  writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify(defaults));
}
